import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { makeStyles } from "@material-ui/styles";
import { Grid, Card, CardContent, Typography, Chip } from "@material-ui/core";
import {
	formatDate,
	isNotNullOrEmpty,
	capitalizeFirstLetter,
	isStaffUser,
	useLookup,
	useTerminology,
} from "utils";
import {
	AttachmentListing,
	CrudOptions,
	PushPinIcon,
	StaffName,
	TextClip,
} from "frame/components";

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
		position: "relative",
		marginBottom: theme.spacing(2),
	},
	container: {
		margin: theme.spacing(0.5),
	},
	content: {
		padding: theme.spacing(2),
		flexGrow: 1,
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		[theme.breakpoints.down("sm")]: {
			width: "100%",
			flexWrap: "wrap",
		},
		"&:last-child": {
			paddingBottom: theme.spacing(2),
		},
	},
	label: {
		padding: theme.spacing(0, 0, 0.5, 0),
		color: theme.palette.text.tertiary,
	},
	value: {
		textOverflow: "ellipsis",
		overflow: "hidden",
		padding: theme.spacing(0, 0, 0.5, 0),
	},
	statusChip: {
		height: "24px",
		fontSize: "0.75rem",
		marginLeft: theme.spacing(1),
		backgroundColor: theme.palette.warning.main,
		color: theme.palette.warning.contrastText
	},
	pinnedChip: {
		height: "24px",
		fontSize: "0.75rem",
		color: theme.palette.brand,
		borderColor: theme.palette.brand,
		backgroundColor: theme.palette.white,
		"& .MuiChip-icon": {
			color: theme.palette.brand,
		},
	},
	titleRow: {
		display: "flex",
		flexWrap: "wrap",
		alignItems: "center",
	},
}));

const JobCard = ({
	job,
	className,
	onEdit,
	onDelete,
	onEmail,
	currentUserId,
	role,
	hasNetwork,
	disableCrud,
	enableSitePin,
	onToggleSitePin,
	enableCustomerPin,
	onToggleCustomerPin,
	enableStaffPin,
	onToggleStaffPin,
	...rest
}) => {
	const classes = useStyles();
	const { siteLabel, customerLabel } = useTerminology();

	const editable = isStaffUser(role) ? job.creatorId === currentUserId : true;

	const quickPicCustomer = useLookup(
		job.customerId,
		"customers",
		"customerName"
	);
	const quickPicSite = useLookup(job.siteId, "sites", "siteName");
	const quickPicStaff = useLookup(job.staffId, "staff", "fullName");

	const typeLabels = {
		site: siteLabel,
		customer: customerLabel,
		staff: "Staff",
	};
	const typeLabel =
		typeLabels[job.type] || capitalizeFirstLetter(job.type);

	if (!job) {
		return null;
	}

	return (
		<Card
			{...rest}
			data-cy={`job-${job.title}`}
			className={clsx(classes.root, className)}
		>
			<CardContent className={classes.content}>
				<Grid className={classes.container} container spacing={2}>
					<Grid item md={4} sm={6} xs={12}>
						<Typography variant="body2" className={classes.label}>
							Title
						</Typography>
						<Typography variant="h6" className={classes.value} component="div">
							<span className={classes.titleRow}>
								{job.title}
								{job.syncing && (
									<Chip
										size="small"
										label="Pending sync"
										className={classes.statusChip}
									/>
								)}
							</span>
						</Typography>
					</Grid>
					<Grid item md={4} sm={6} xs={12}>
						<Typography variant="body2" className={classes.label}>
							Type
						</Typography>
						<Typography variant="h6" className={classes.value}>
							{typeLabel}
							{job.type === "customer" && quickPicCustomer ? `: ${quickPicCustomer}` : ""}
							{job.type === "site" && quickPicSite ? `: ${quickPicSite}` : ""}
							{job.type === "staff" && quickPicStaff ? `: ${quickPicStaff}` : ""}
						</Typography>
					</Grid>
					<Grid item md={4} sm={6} xs={12}>
						{(job.pinnedToSite || job.pinnedToCustomer || job.pinnedToStaff) && (
							<>
								<Typography variant="body2" className={classes.label}>
									Pin
								</Typography>
								<Typography
									variant="h6"
									className={classes.value}
									component="div"
								>
									{job.pinnedToSite && (
										<Chip
											size="small"
											variant="outlined"
											icon={<PushPinIcon />}
											label={`Pinned to ${siteLabel}`}
											className={classes.pinnedChip}
											data-cy="pinned-to-site"
										/>
									)}
									{job.pinnedToCustomer && (
										<Chip
											size="small"
											variant="outlined"
											icon={<PushPinIcon />}
											label={`Pinned to ${customerLabel}`}
											className={classes.pinnedChip}
											data-cy="pinned-to-customer"
										/>
									)}
									{job.pinnedToStaff && (
										<Chip
											size="small"
											variant="outlined"
											icon={<PushPinIcon />}
											label="Pinned to Staff"
											className={classes.pinnedChip}
											data-cy="pinned-to-staff"
										/>
									)}
								</Typography>
							</>
						)}
						<Typography variant="body2" className={classes.label}>
							Notes
						</Typography>
						<Typography variant="h6" className={classes.value}>
							<TextClip
								text={job.notes}
								numberOfCharacters={140}
							/>
						</Typography>
					</Grid>
					<Grid item md={4} sm={6} xs={12}>
						<Typography variant="body2" className={classes.label}>
							Attachments
						</Typography>
						{isNotNullOrEmpty(job.attachments) ? (
							<AttachmentListing attachments={job.attachments} />
						) : (
							"-"
						)}
					</Grid>
					<Grid item md={4} sm={6} xs={12}>
						<Typography variant="body2" className={classes.label}>
							Created By
						</Typography>
						<Typography variant="h6" className={classes.value}>
							{job.creatorName || (job.creatorId ? <StaffName id={job.creatorId} /> : "-")}
						</Typography>
					</Grid>
					<Grid item md={4} sm={6} xs={12}>
						<Typography variant="body2" className={classes.label}>
							Created Date
						</Typography>
						<Typography variant="h6" className={classes.value}>
							{formatDate(job.createdAt)}
						</Typography>
					</Grid>
				</Grid>
			</CardContent>
			{editable && !disableCrud && (
				<CrudOptions
					onDelete={() => onDelete(job.id)}
					onEdit={() => onEdit(job.id)}
					onEmail={onEmail ? () => onEmail(job.id) : undefined}
					customOptions={[
						...(enableSitePin && hasNetwork && onToggleSitePin
							? [
									{
										label: job.pinnedToSite
											? `Unpin from ${siteLabel}`
											: `Pin to ${siteLabel}`,
										icon: PushPinIcon,
										onClick: () => onToggleSitePin(job),
									},
							  ]
							: []),
						...(enableCustomerPin && hasNetwork && onToggleCustomerPin
							? [
									{
										label: job.pinnedToCustomer
											? `Unpin from ${customerLabel}`
											: `Pin to ${customerLabel}`,
										icon: PushPinIcon,
										onClick: () => onToggleCustomerPin(job),
									},
							  ]
							: []),
						...(enableStaffPin && hasNetwork && onToggleStaffPin
							? [
									{
										label: job.pinnedToStaff
											? "Unpin from Staff"
											: "Pin to Staff",
										icon: PushPinIcon,
										onClick: () => onToggleStaffPin(job),
									},
							  ]
							: []),
					]}
				/>
			)}
		</Card>
	);
};

JobCard.propTypes = {
	className: PropTypes.string,
	job: PropTypes.object.isRequired,
	type: PropTypes.string,
	onEdit: PropTypes.func,
	onDelete: PropTypes.func,
	currentUserId: PropTypes.string.isRequired,
	role: PropTypes.string.isRequired,
	hasNetwork: PropTypes.bool.isRequired,
	disableCrud: PropTypes.bool,
	enableSitePin: PropTypes.bool,
	onToggleSitePin: PropTypes.func,
	enableCustomerPin: PropTypes.bool,
	onToggleCustomerPin: PropTypes.func,
	enableStaffPin: PropTypes.bool,
	onToggleStaffPin: PropTypes.func,
};

export default JobCard;
